import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, Users, X, Tag as TagIcon, Plus } from 'lucide-react';
import {
  listLeads,
  listLeadTags,
  MarketingLead,
  MarketingLeadSource,
} from '@/services/marketingLeadService';

export type CampaignTargetType = 'ALL_LEADS' | 'SELECTED_LEADS' | 'FILTERED_LEADS';

export interface RecipientSelection {
  targetType: CampaignTargetType;
  // For SELECTED_LEADS: array of lead ids
  // For FILTERED_LEADS: a single JSON-stringified filter spec wrapped in an array
  targetIds: string[];
}

interface FilterSpec {
  tags?: string[];
  source?: MarketingLeadSource;
}

interface Props {
  value: RecipientSelection;
  onChange: (next: RecipientSelection) => void;
}

const SOURCE_OPTIONS: { value: MarketingLeadSource; label: string }[] = [
  { value: 'MANUAL', label: 'Ajout manuel' },
  { value: 'CSV_IMPORT', label: 'Import Excel/CSV' },
  { value: 'FORMER_USER', label: 'Anciens utilisateurs' },
  { value: 'PUBLIC_FORM', label: 'Formulaire public' },
  { value: 'API', label: 'API' },
];

export const RecipientSelector: React.FC<Props> = ({ value, onChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<MarketingLead[]>([]);
  const [searching, setSearching] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [availableTags, setAvailableTags] = useState<{ tag: string; count: number }[]>([]);

  const initialSpec: FilterSpec = useMemo(() => {
    if (value.targetType !== 'FILTERED_LEADS') return {};
    try {
      return JSON.parse(value.targetIds[0] ?? '{}');
    } catch {
      return {};
    }
  }, [value]);

  const [selectedTags, setSelectedTags] = useState<string[]>(initialSpec.tags ?? []);
  const [sourceFilter, setSourceFilter] = useState<MarketingLeadSource | ''>(
    initialSpec.source ?? '',
  );
  const [newTagInput, setNewTagInput] = useState('');

  const [selectedLeads, setSelectedLeads] = useState<MarketingLead[]>([]);

  // Load tag catalogue on mount
  useEffect(() => {
    listLeadTags().then(setAvailableTags).catch(() => setAvailableTags([]));
  }, []);

  // Initial load of selected leads (when editing an existing campaign)
  useEffect(() => {
    if (value.targetType !== 'SELECTED_LEADS' || value.targetIds.length === 0) {
      setSelectedLeads([]);
      return;
    }
    listLeads({ limit: 200 })
      .then((r) =>
        setSelectedLeads(
          r.data.filter((l) => value.targetIds.includes(l.id)),
        ),
      )
      .catch(() => setSelectedLeads([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live search for SELECTED_LEADS
  useEffect(() => {
    if (value.targetType !== 'SELECTED_LEADS') return;
    const t = setTimeout(async () => {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        return;
      }
      setSearching(true);
      try {
        const r = await listLeads({ search: searchTerm.trim(), status: 'ACTIVE', limit: 20 });
        setSearchResults(r.data);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [searchTerm, value.targetType]);

  // Compute live count
  useEffect(() => {
    let cancelled = false;
    const compute = async () => {
      if (value.targetType === 'SELECTED_LEADS') {
        setCount(selectedLeads.length);
        return;
      }
      const filters: Parameters<typeof listLeads>[0] = { status: 'ACTIVE', limit: 1 };
      if (value.targetType === 'FILTERED_LEADS') {
        if (selectedTags.length > 0) filters.tags = selectedTags;
        if (sourceFilter) filters.source = sourceFilter;
      }
      try {
        const r = await listLeads(filters);
        if (!cancelled) setCount(r.meta.total);
      } catch {
        if (!cancelled) setCount(null);
      }
    };
    compute();
    return () => {
      cancelled = true;
    };
  }, [value.targetType, selectedTags, sourceFilter, selectedLeads.length]);

  const setTargetType = (t: CampaignTargetType) => {
    if (t === 'ALL_LEADS') onChange({ targetType: t, targetIds: [] });
    else if (t === 'SELECTED_LEADS') onChange({ targetType: t, targetIds: selectedLeads.map((l) => l.id) });
    else
      onChange({
        targetType: t,
        targetIds: [JSON.stringify({ tags: selectedTags, source: sourceFilter || undefined })],
      });
  };

  const updateFilter = (next: { tags?: string[]; source?: MarketingLeadSource | '' }) => {
    const tags = next.tags ?? selectedTags;
    const src = next.source !== undefined ? next.source : sourceFilter;
    onChange({
      targetType: 'FILTERED_LEADS',
      targetIds: [JSON.stringify({ tags, source: src || undefined })],
    });
  };

  const toggleTag = (t: string) => {
    const next = selectedTags.includes(t)
      ? selectedTags.filter((x) => x !== t)
      : [...selectedTags, t];
    setSelectedTags(next);
    updateFilter({ tags: next });
  };

  const addCustomTag = () => {
    const v = newTagInput.trim();
    if (!v || selectedTags.includes(v)) {
      setNewTagInput('');
      return;
    }
    const next = [...selectedTags, v];
    setSelectedTags(next);
    updateFilter({ tags: next });
    setNewTagInput('');
  };

  const removeTag = (t: string) => {
    const next = selectedTags.filter((x) => x !== t);
    setSelectedTags(next);
    updateFilter({ tags: next });
  };

  const addLead = (l: MarketingLead) => {
    if (selectedLeads.find((x) => x.id === l.id)) return;
    const next = [...selectedLeads, l];
    setSelectedLeads(next);
    onChange({ targetType: 'SELECTED_LEADS', targetIds: next.map((x) => x.id) });
  };
  const removeLead = (id: string) => {
    const next = selectedLeads.filter((l) => l.id !== id);
    setSelectedLeads(next);
    onChange({ targetType: 'SELECTED_LEADS', targetIds: next.map((x) => x.id) });
  };

  // Tags not yet selected (for the chip suggestions)
  const unselectedTags = availableTags.filter((t) => !selectedTags.includes(t.tag));

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-1">
        <Users size={16} /> Destinataires
      </h4>
      <div className="flex flex-wrap gap-2">
        {(['ALL_LEADS', 'SELECTED_LEADS', 'FILTERED_LEADS'] as CampaignTargetType[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTargetType(t)}
            className={`px-3 py-1.5 text-sm rounded-lg border transition ${
              value.targetType === t
                ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
            }`}
          >
            {t === 'ALL_LEADS' && 'Tous les leads actifs'}
            {t === 'SELECTED_LEADS' && 'Leads sélectionnés'}
            {t === 'FILTERED_LEADS' && 'Leads filtrés'}
          </button>
        ))}
      </div>

      {value.targetType === 'SELECTED_LEADS' && (
        <div className="space-y-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un lead par email ou nom…"
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
            />
            {searching && (
              <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
            )}
          </div>
          {searchResults.length > 0 && (
            <ul className="bg-white border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-100">
              {searchResults.map((l) => (
                <li key={l.id}>
                  <button
                    type="button"
                    onClick={() => addLead(l)}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                  >
                    <span className="font-medium">{l.fullName || l.email}</span>{' '}
                    <span className="text-gray-500 text-xs">{l.email}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {selectedLeads.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedLeads.map((l) => (
                <span
                  key={l.id}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded"
                >
                  {l.fullName || l.email}
                  <button onClick={() => removeLead(l.id)}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {value.targetType === 'FILTERED_LEADS' && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1.5 font-medium">
              Filtrer par tags
            </label>
            {selectedTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selectedTags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-indigo-600 text-white rounded-full"
                  >
                    <TagIcon size={10} />
                    {t}
                    <button onClick={() => removeTag(t)} type="button">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {unselectedTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {unselectedTags.map(({ tag, count }) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white text-gray-700 border border-gray-300 rounded-full hover:bg-indigo-50 hover:border-indigo-300 transition"
                  >
                    <TagIcon size={10} />
                    {tag}
                    <span className="text-gray-400">({count})</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomTag();
                  }
                }}
                placeholder="Ajouter un tag personnalisé"
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
              />
              <button
                type="button"
                onClick={addCustomTag}
                disabled={!newTagInput.trim()}
                className="px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 inline-flex items-center gap-1 text-sm"
              >
                <Plus size={14} />
                Ajouter
              </button>
            </div>
            {availableTags.length === 0 && (
              <p className="text-xs text-gray-500 italic mt-1">
                Aucun tag défini. Utilisez la page Leads pour étiqueter vos contacts.
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1.5 font-medium">Source</label>
            <select
              value={sourceFilter}
              onChange={(e) => {
                const v = e.target.value as MarketingLeadSource | '';
                setSourceFilter(v);
                updateFilter({ source: v });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
            >
              <option value="">Toutes les sources</option>
              {SOURCE_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg p-3 border border-gray-200">
        <p className="text-sm text-gray-700">
          {count == null ? (
            <span className="text-gray-400 inline-flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" /> Calcul du nombre…
            </span>
          ) : (
            <>
              <strong className="text-indigo-700">{count}</strong> lead{count > 1 ? 's' : ''} correspond
              {count > 1 ? 'ent' : ''} à cette sélection.
              <span className="block text-xs text-gray-500 mt-0.5">
                Les utilisateurs déjà inscrits sur la plateforme et les désinscrits seront automatiquement retirés au moment de l'envoi.
              </span>
            </>
          )}
        </p>
      </div>
    </div>
  );
};
